import { useEffect, useLayoutEffect, useRef, useState, type ComponentProps } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import { orderBy } from 'firebase/firestore';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  History,
  Maximize,
  Pencil,
  Plus,
  RotateCcw,
  Sparkles,
  Trash2,
} from 'lucide-react';
import {
  canProject,
  OUTLINE_VISUAL_KINDS,
  SLIDE_HEIGHT,
  SLIDE_WIDTH,
  TRAINING_MAX_SLIDES,
  TRAINING_SESSION_STATUS_LABEL,
  type DeckVersion,
  type OutlineSlide,
  type SessionOutline,
  type TrainingSession,
} from '@allemnii-pulse/shared';
import { useProject } from '../projects/ProjectProvider.js';
import { useCollection, useDocument } from '../../lib/firestore.js';
import { api, ApiError } from '../../lib/api.js';
import { asDate, relativeTime } from '../../lib/time.js';
import { Card } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Textarea } from '../../components/ui/Textarea.js';
import { Dialog, DialogHeader, DialogTitle } from '../../components/ui/Dialog.js';
import { UserName } from '../../components/UserName.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/Select.js';
import { GenerateDialog, type GenerateRequest } from './GenerateDialog.js';
import { TrainingExportMenu } from './TrainingExportMenu.js';
import { deckImagePaths, SlideView, useDeckFonts, useSlideImageUrls } from './SlideView.js';
import { postJob, useJobRunner, useTrainingState } from './useTraining.js';

/** The width of an element, kept current as it resizes. */
export function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry!.contentRect.width));
    observer.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

/**
 * A SlideView as wide as its box, up to `maxWidth` — for grids, where a fixed-width slide would
 * force its column (and on a phone, the dialog) wider than the screen. min-w-0 lets the grid
 * column shrink below the slide's current width, which the observer then follows.
 */
function FluidSlideView({
  maxWidth = Infinity,
  ...props
}: Omit<ComponentProps<typeof SlideView>, 'width'> & { maxWidth?: number }) {
  const [ref, width] = useElementWidth<HTMLDivElement>();
  return (
    <div ref={ref} className="min-w-0">
      {width > 0 && <SlideView {...props} width={Math.min(width, maxWidth)} />}
    </div>
  );
}

const NO_VISUAL = 'none';

function OutlineEditor({
  outline,
  editable,
  onChange,
}: {
  outline: SessionOutline;
  editable: boolean;
  onChange: (outline: SessionOutline) => void;
}) {
  const setSlide = (i: number, patch: Partial<OutlineSlide>) =>
    onChange({
      ...outline,
      slides: outline.slides.map((s, si) => (si === i ? { ...s, ...patch } : s)),
    });
  const move = (i: number, delta: number) => {
    const slides = [...outline.slides];
    const [s] = slides.splice(i, 1);
    slides.splice(i + delta, 0, s!);
    onChange({ ...outline, slides });
  };

  return (
    <div className="flex flex-col gap-3">
      <Input
        value={outline.title}
        maxLength={200}
        disabled={!editable}
        onChange={(e) => onChange({ ...outline, title: e.target.value })}
        className="h-auto border-none bg-transparent px-1 text-base font-semibold"
        aria-label="Session title"
      />
      {outline.slides.map((slide, i) => (
        <Card key={i} className="flex flex-col gap-2 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-muted">{i + 1}</span>
            <Input
              value={slide.headline}
              maxLength={200}
              disabled={!editable}
              placeholder="Slide headline"
              onChange={(e) => setSlide(i, { headline: e.target.value })}
              className="h-auto min-w-48 flex-1 border-none bg-transparent px-1 font-medium"
            />
            {editable && (
              // Wraps under the headline on a phone; ml-auto then right-aligns it (beside the
              // flex-1 headline on wider screens it has no effect).
              <div className="ml-auto flex">
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="Move up"
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                  className="h-10 w-10 md:h-8 md:w-8"
                >
                  <ArrowUp />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="Move down"
                  disabled={i === outline.slides.length - 1}
                  onClick={() => move(i, 1)}
                  className="h-10 w-10 md:h-8 md:w-8"
                >
                  <ArrowDown />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="Delete slide"
                  className="h-10 w-10 md:h-8 md:w-8"
                  disabled={outline.slides.length === 1}
                  onClick={() =>
                    onChange({ ...outline, slides: outline.slides.filter((_, si) => si !== i) })
                  }
                >
                  <Trash2 />
                </Button>
              </div>
            )}
          </div>
          <Textarea
            rows={2}
            maxLength={1500}
            disabled={!editable}
            value={slide.description}
            placeholder="What this slide says"
            onChange={(e) => setSlide(i, { description: e.target.value })}
          />
          <div className="flex flex-wrap items-start gap-2">
            <div className="w-40">
              <Select
                value={slide.visual?.kind ?? NO_VISUAL}
                disabled={!editable}
                onValueChange={(v) =>
                  setSlide(i, {
                    visual:
                      v === NO_VISUAL
                        ? null
                        : {
                            kind: v as (typeof OUTLINE_VISUAL_KINDS)[number],
                            description: slide.visual?.description ?? '',
                          },
                  })
                }
              >
                <SelectTrigger aria-label="Visual">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_VISUAL}>No visual</SelectItem>
                  {OUTLINE_VISUAL_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {k[0]!.toUpperCase() + k.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {slide.visual && (
              <Textarea
                rows={2}
                maxLength={1000}
                disabled={!editable}
                className="min-w-[min(240px,100%)] flex-1"
                value={slide.visual.description}
                placeholder="What the visual shows"
                onChange={(e) =>
                  setSlide(i, { visual: { ...slide.visual!, description: e.target.value } })
                }
              />
            )}
          </div>
        </Card>
      ))}
      {editable && (
        <Button
          className="self-start"
          disabled={outline.slides.length >= TRAINING_MAX_SLIDES}
          onClick={() =>
            onChange({
              ...outline,
              slides: [...outline.slides, { headline: '', description: '', visual: null }],
            })
          }
        >
          <Plus /> Add slide
        </Button>
      )}
    </div>
  );
}

function outlineProblem(outline: SessionOutline): string | null {
  if (!outline.title.trim()) return 'The session needs a title.';
  for (const [i, s] of outline.slides.entries()) {
    if (!s.headline.trim() || !s.description.trim())
      return `Slide ${i + 1} needs a headline and a description.`;
    if (s.visual && !s.visual.description.trim())
      return `Describe the visual on slide ${i + 1}, or remove it.`;
  }
  return null;
}

/** Whether `ref`'s element is the page's full-screen element, kept current. */
function useIsFullscreen(ref: React.RefObject<HTMLElement | null>) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const update = () => setOn(!!ref.current && document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', update);
    return () => document.removeEventListener('fullscreenchange', update);
  }, [ref]);
  return on;
}

/** The window size, kept current — the full-screen slide is fitted to it. */
function useWindowSize() {
  const [size, setSize] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const update = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return size;
}

/** Horizontal travel, in pixels, that makes a touch on the presented slide a swipe. */
const SWIPE_DISTANCE = 50;

/**
 * Presenting a deck full screen: the slide as large as the screen allows on black. Arrow keys,
 * Space, PageUp/PageDown and Home/End move between slides, a click advances, a swipe moves
 * either way, Esc leaves (the browser's own exit). Shares the slide index with the preview, so
 * leaving lands on the slide that was being shown.
 *
 * `overlay` is the stand-in where the Fullscreen API isn't available (iPhone Safari only
 * fullscreens video): the same view as a fixed layer over the page, portalled to <body> so no
 * scrolling or stacking ancestor can clip it.
 */
function FullscreenDeck({
  deck,
  imageUrls,
  index,
  onIndex,
  presentRef,
  active,
  overlay,
  onExit,
}: {
  deck: DeckVersion['deck'];
  imageUrls: Record<string, string>;
  index: number;
  onIndex: (index: number) => void;
  presentRef: React.RefObject<HTMLDivElement | null>;
  active: boolean;
  overlay: boolean;
  onExit: () => void;
}) {
  const { w, h } = useWindowSize();
  const last = deck.slides.length - 1;
  const touchX = useRef<number | null>(null);
  const next = () => onIndex(Math.min(last, index + 1));
  const prev = () => onIndex(Math.max(0, index - 1));

  useEffect(() => {
    if (!active) return;
    function onKey(e: KeyboardEvent) {
      const next = ['ArrowRight', 'ArrowDown', 'PageDown', ' ', 'Enter'];
      const prev = ['ArrowLeft', 'ArrowUp', 'PageUp', 'Backspace'];
      if (next.includes(e.key)) onIndex(Math.min(last, index + 1));
      else if (prev.includes(e.key)) onIndex(Math.max(0, index - 1));
      else if (e.key === 'Home') onIndex(0);
      else if (e.key === 'End') onIndex(last);
      // Real full screen handles Esc itself; the overlay has to.
      else if (e.key === 'Escape' && overlay) onExit();
      else return;
      e.preventDefault();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, overlay, index, last, onIndex, onExit]);

  const content = active && (
    <>
      <button
        type="button"
        aria-label="Next slide"
        className="cursor-pointer"
        onClick={next}
        onTouchStart={(e) => {
          touchX.current = e.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          const start = touchX.current;
          touchX.current = null;
          const end = e.changedTouches[0]?.clientX;
          if (start === null || end === undefined || Math.abs(end - start) < SWIPE_DISTANCE) return;
          // A swipe, not a tap: don't let the click that follows also advance.
          e.preventDefault();
          if (end < start) next();
          else prev();
        }}
      >
        {/* Keyed by slide, so each slide mounts fresh and plays its entrance. */}
        <SlideView
          key={deck.slides[index]!.id}
          slide={deck.slides[index]!}
          design={deck.design}
          imageUrls={imageUrls}
          width={Math.floor(Math.min(w, (h * SLIDE_WIDTH) / SLIDE_HEIGHT))}
          slideNumber={index + 1}
          animate
        />
      </button>
      <div className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 flex items-center gap-2 rounded-md bg-black/60 px-2 py-1 text-xs text-white/80">
        {/* No keyboard on a phone: explicit back/forward alongside tap and swipe. */}
        <button
          type="button"
          aria-label="Previous slide"
          disabled={index === 0}
          className="rounded p-2 disabled:opacity-40 md:hidden"
          onClick={prev}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="font-mono">
          {index + 1} / {deck.slides.length}
        </span>
        <button
          type="button"
          aria-label="Next slide"
          disabled={index === last}
          className="rounded p-2 disabled:opacity-40 md:hidden"
          onClick={next}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <button
          type="button"
          className="rounded px-3 py-2 hover:bg-white/10 md:px-1.5 md:py-0.5"
          onClick={onExit}
        >
          Exit<span className="hidden md:inline"> (Esc)</span>
        </button>
      </div>
    </>
  );

  // Always mounted (an empty div until it's the full-screen element), so it can be the target
  // of requestFullscreen from the button.
  return (
    <>
      <div
        ref={presentRef}
        className={active && !overlay ? 'flex items-center justify-center bg-black' : ''}
      >
        {!overlay && content}
      </div>
      {overlay &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}

function DeckViewer({ projectId, version }: { projectId: string; version: DeckVersion }) {
  const deck = version.deck;
  const [index, setIndex] = useState(0);
  const [containerRef, width] = useElementWidth<HTMLDivElement>();
  const presentRef = useRef<HTMLDivElement>(null);
  const fullscreen = useIsFullscreen(presentRef);
  const [overlay, setOverlay] = useState(false);
  useDeckFonts([deck]);
  const imageUrls = useSlideImageUrls(projectId, deckImagePaths(deck));
  const current = Math.min(index, deck.slides.length - 1);
  const slide = deck.slides[current]!;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="mr-auto font-mono text-xs text-muted">
          Slide {current + 1} / {deck.slides.length}
        </span>
        <Button
          size="sm"
          className="h-10 md:h-7"
          onClick={() => {
            if (document.fullscreenEnabled) void presentRef.current?.requestFullscreen();
            else setOverlay(true);
          }}
        >
          <Maximize /> Full screen
        </Button>
      </div>
      <FullscreenDeck
        deck={deck}
        imageUrls={imageUrls}
        index={current}
        onIndex={setIndex}
        presentRef={presentRef}
        active={fullscreen || overlay}
        overlay={overlay}
        onExit={() => {
          if (overlay) setOverlay(false);
          else void document.exitFullscreen();
        }}
      />
      <div ref={containerRef} className="w-full">
        {width > 0 && (
          <SlideView
            slide={slide}
            design={deck.design}
            imageUrls={imageUrls}
            width={width}
            slideNumber={current + 1}
            className="rounded-lg border border-border"
          />
        )}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {deck.slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setIndex(i)}
            className={`shrink-0 rounded-md border-2 ${i === current ? 'border-accent' : 'border-transparent'}`}
            aria-label={`Slide ${i + 1}`}
          >
            <SlideView
              slide={s}
              design={deck.design}
              imageUrls={imageUrls}
              width={144}
              slideNumber={i + 1}
              className="rounded"
            />
          </button>
        ))}
      </div>
      {slide.speakerNotes && (
        <Card className="p-3">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">
            Speaker notes
          </span>
          <p className="mt-1 whitespace-pre-wrap text-sm">{slide.speakerNotes}</p>
        </Card>
      )}
    </div>
  );
}

const NEWEST_FIRST = [orderBy('versionNumber', 'desc')];

const SOURCE_LABEL: Record<DeckVersion['source'], string> = {
  llm: 'Generated',
  manual: 'Edited',
  restore: 'Restored',
};

function HistoryDialog({
  projectId,
  session,
  canManage,
  open,
  onClose,
}: {
  projectId: string;
  session: TrainingSession;
  canManage: boolean;
  open: boolean;
  onClose: () => void;
}) {
  const { data: versions } = useCollection<DeckVersion>(
    open ? `projects/${projectId}/trainingSessions/${session.id}/deckVersions` : null,
    NEWEST_FIRST,
  );
  const [preview, setPreview] = useState<DeckVersion | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const imageUrls = useSlideImageUrls(projectId, deckImagePaths(preview?.deck));
  useDeckFonts([preview?.deck]);

  async function restore(v: DeckVersion) {
    if (
      !window.confirm(
        `Restore version ${v.versionNumber}? It becomes the newest version; nothing is deleted.`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      await api.post(
        `/projects/${projectId}/training/sessions/${session.id}/deck/versions/${v.id}/restore`,
      );
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not restore this version.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} className="max-w-4xl">
      <DialogHeader>
        <DialogTitle>Slide history</DialogTitle>
      </DialogHeader>
      {/* On a phone the dialog itself is the one scroller; the version list is capped instead,
          so a picked version's preview shows just below it. */}
      <div className="mt-4 flex flex-col gap-4 md:max-h-[70vh] md:flex-row md:overflow-y-auto">
        <div className="flex max-h-48 flex-col gap-1 overflow-y-auto md:max-h-none md:w-64 md:shrink-0 md:overflow-visible">
          {versions.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setPreview(v)}
              className={`flex flex-col rounded-lg border px-3 py-2 text-left text-sm ${
                preview?.id === v.id
                  ? 'border-accent'
                  : 'border-border hover:border-[var(--ink-400)]'
              }`}
            >
              <span className="font-medium">
                v{v.versionNumber} · {SOURCE_LABEL[v.source]}
                {v.id === session.currentDeckVersionId ? ' · current' : ''}
              </span>
              <span className="text-xs text-muted">
                <UserName uid={v.authorUid} /> · {relativeTime(v.createdAt)}
              </span>
              {v.changeSummary && <span className="text-xs text-muted">{v.changeSummary}</span>}
            </button>
          ))}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {preview ? (
            <>
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
                {preview.deck.slides.map((s, i) => (
                  <FluidSlideView
                    key={s.id}
                    slide={s}
                    design={preview.deck.design}
                    imageUrls={imageUrls}
                    maxWidth={200}
                    slideNumber={i + 1}
                    className="rounded border border-border"
                  />
                ))}
              </div>
              {canManage && preview.id !== session.currentDeckVersionId && (
                <Button
                  className="self-start"
                  disabled={busy}
                  onClick={() => void restore(preview)}
                >
                  <RotateCcw /> Restore v{preview.versionNumber}
                </Button>
              )}
            </>
          ) : (
            <p className="text-sm text-muted">Pick a version to preview it.</p>
          )}
          {error && <p className="text-sm text-danger">{error}</p>}
        </div>
      </div>
    </Dialog>
  );
}

type DialogKind = 'outline' | 'outline-feedback' | 'confirm' | 'slides-feedback';

/**
 * One session: its plan, the outline (generate → edit by hand / regenerate with feedback →
 * confirm, which starts the slides), then the slides themselves with speaker notes, history,
 * export and the editor. A stale flag from newly added sources shows as a banner.
 */
export function TrainingSessionPage() {
  const { projectId, role } = useProject();
  const { sessionId } = useParams<{ sessionId: string }>();
  const canManage = canProject(role, 'training.manage');
  const { data: state } = useTrainingState(projectId);
  const { data: session, loading } = useDocument<TrainingSession>(
    sessionId ? `projects/${projectId}/trainingSessions/${sessionId}` : null,
  );
  const { data: version } = useDocument<DeckVersion>(
    session?.currentDeckVersionId
      ? `projects/${projectId}/trainingSessions/${sessionId}/deckVersions/${session.currentDeckVersionId}`
      : null,
  );
  const [outline, setOutline] = useState<SessionOutline | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const job = useJobRunner(() => setDirty(false));

  useEffect(() => {
    if (!dirty) setOutline(session?.outline ?? null);
  }, [session, dirty]);

  if (loading) return <p className="p-6 text-sm text-muted">Loading…</p>;
  if (!session || !sessionId) return <p className="p-6 text-sm text-muted">Session not found.</p>;

  const generating = session.status === 'generating';
  const outlineConfirmed = !!session.outlineConfirmedAt;
  const editable = canManage && !generating && !job.running;
  const base = `/sessions/${sessionId}`;

  async function saveOutline(): Promise<boolean> {
    if (!outline) return false;
    const problem = outlineProblem(outline);
    if (problem) {
      setError(problem);
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      await api.put(`/projects/${projectId}/training${base}/outline`, { outline });
      setDirty(false);
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the outline.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  function run(kind: DialogKind, request: GenerateRequest) {
    if (kind === 'outline' || kind === 'outline-feedback') {
      if (dirty && !window.confirm('Regenerating replaces your unsaved edits. Continue?')) return;
      void job.start(
        () => postJob(projectId, `${base}/outline/generate`, request),
        'Could not start generating the outline.',
      );
    } else if (kind === 'confirm') {
      void job.start(async () => {
        // Unsaved edits are saved first; if that fails, stop here with the edits still on screen.
        if (dirty && !(await saveOutline()))
          throw new Error('Fix the outline before confirming it.');
        return postJob(projectId, `${base}/outline/confirm`, request);
      }, 'Fix the outline before confirming it.');
    } else {
      void job.start(
        () => postJob(projectId, `${base}/slides/generate`, request),
        'Could not start generating the slides.',
      );
    }
  }

  async function dismissStale() {
    await api
      .post(`/projects/${projectId}/training${base}/dismiss-stale`)
      .catch(() => setError('Could not dismiss the flag.'));
  }

  const dialogCopy: Record<
    DialogKind,
    { title: string; submit: string; feedback: 'none' | 'required' }
  > = {
    outline: { title: 'Generate the outline', submit: 'Generate', feedback: 'none' },
    'outline-feedback': {
      title: 'Regenerate the outline',
      submit: 'Regenerate',
      feedback: 'required',
    },
    confirm: {
      title: 'Confirm the outline and generate the slides',
      submit: 'Confirm & generate slides',
      feedback: 'none',
    },
    'slides-feedback': {
      title: 'Regenerate the slides',
      submit: 'Regenerate',
      feedback: 'required',
    },
  };

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-6">
      <div className="flex flex-wrap items-start gap-3">
        <div className="mr-auto min-w-0">
          <Link
            to={`/projects/${projectId}/sessions`}
            className="text-xs text-muted hover:text-text"
          >
            ← Sessions
          </Link>
          <h1 className="break-words font-heading text-2xl font-semibold">
            {session.order + 1}. {session.title}
          </h1>
          <p className="mt-1 text-xs text-muted">
            {session.date ?? 'Not scheduled'} · {session.durationMinutes} min · {session.slideCount}{' '}
            slides · {TRAINING_SESSION_STATUS_LABEL[session.status]}
          </p>
        </div>
        {version && <TrainingExportMenu projectId={projectId} sessionId={sessionId} />}
      </div>

      {session.stale && (
        <Card className="flex flex-wrap items-center gap-3 border-danger/40 p-3 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0 text-danger" />
          <span className="min-w-0 flex-1">
            <span className="font-medium">New sources may change this session.</span>{' '}
            {session.stale.reason}
          </span>
          {canManage && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={!editable} onClick={() => setDialog('outline')}>
                Regenerate outline
              </Button>
              {outlineConfirmed && (
                <Button size="sm" disabled={!editable} onClick={() => setDialog('slides-feedback')}>
                  Regenerate slides
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => void dismissStale()}>
                Dismiss
              </Button>
            </div>
          )}
        </Card>
      )}

      <Card className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
        <div>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">
            Summary
          </span>
          <p className="mt-1 break-words text-sm">{session.summary}</p>
        </div>
        <div>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">
            Learning objectives
          </span>
          <ul className="mt-1 list-disc break-words pl-5 text-sm">
            {session.learningObjectives.map((o) => (
              <li key={o}>{o}</li>
            ))}
          </ul>
        </div>
      </Card>

      {(job.running || generating) && (
        <Card className="p-4 text-sm text-muted">
          {generating ? 'Generating the slides — this takes a minute or two…' : 'Working…'}
        </Card>
      )}
      {job.error && <Card className="border-danger p-4 text-sm text-danger">{job.error}</Card>}
      {session.slidesError && !generating && (
        <Card className="border-danger p-4 text-sm text-danger">
          Slide generation failed: {session.slidesError}
        </Card>
      )}

      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="mr-auto text-lg font-semibold">Outline</h2>
          {canManage && outline && (
            <>
              <Button disabled={!editable} onClick={() => setDialog('outline-feedback')}>
                <Sparkles /> Regenerate with feedback
              </Button>
              {dirty && (
                <Button variant="ghost" onClick={() => setDirty(false)}>
                  Discard changes
                </Button>
              )}
              <Button disabled={!dirty || busy || !editable} onClick={() => void saveOutline()}>
                Save
              </Button>
              {(!outlineConfirmed || dirty) && (
                <Button
                  variant="default"
                  disabled={busy || !editable}
                  onClick={() => setDialog('confirm')}
                >
                  Confirm & generate slides
                </Button>
              )}
            </>
          )}
        </div>
        {outlineConfirmed && !dirty && (
          <p className="text-xs text-muted">
            Confirmed {relativeTime(session.outlineConfirmedAt)}. Editing it puts it back to draft.
          </p>
        )}
        {error && <p className="text-sm text-danger">{error}</p>}
        {outline ? (
          <OutlineEditor
            outline={outline}
            editable={editable}
            onChange={(o) => {
              setOutline(o);
              setDirty(true);
            }}
          />
        ) : (
          <Card className="flex flex-col items-start gap-3 p-4 sm:p-6">
            <p className="text-sm text-muted">
              No outline yet. Gemini writes a headline and short description for each of the{' '}
              {session.slideCount} slides, with an illustration, image or diagram where it helps.
            </p>
            {canManage && (
              <Button
                variant="default"
                disabled={!editable || !state?.sessionsConfirmedAt}
                onClick={() => setDialog('outline')}
              >
                <Sparkles /> Generate outline
              </Button>
            )}
            {!state?.sessionsConfirmedAt && (
              <p className="text-xs text-muted">Confirm the sessions list first.</p>
            )}
          </Card>
        )}
      </section>

      {version && (
        <section className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="mr-auto text-lg font-semibold">
              Slides{' '}
              <span className="text-sm font-normal text-muted">
                v{version.versionNumber} · updated {relativeTime(asDate(version.createdAt))}
              </span>
            </h2>
            <Button onClick={() => setHistoryOpen(true)}>
              <History /> History
            </Button>
            {canManage && (
              <>
                <Button
                  disabled={!editable || !outlineConfirmed}
                  onClick={() => setDialog('slides-feedback')}
                >
                  <Sparkles /> Regenerate with feedback
                </Button>
                <Button asChild variant="default">
                  <Link
                    to={`/projects/${projectId}/sessions/${sessionId}/edit`}
                    aria-disabled={generating}
                  >
                    <Pencil /> Edit slides
                  </Link>
                </Button>
              </>
            )}
          </div>
          <DeckViewer projectId={projectId} version={version} />
        </section>
      )}

      {state && dialog && (
        <GenerateDialog
          open
          onClose={() => setDialog(null)}
          title={dialogCopy[dialog].title}
          submitLabel={dialogCopy[dialog].submit}
          feedback={dialogCopy[dialog].feedback}
          defaults={state.instructions}
          onSubmit={(request) => run(dialog, request)}
        />
      )}
      <HistoryDialog
        projectId={projectId}
        session={session}
        canManage={canManage}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  );
}
